import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Department {
  id: string;
  organization_id: string;
  name: string;
  is_custom: boolean;
  display_order: number;
  created_at: string;
}

export interface UserDepartmentAssignment {
  id: string;
  organization_id: string;
  user_id: string;
  department_id: string | null;
  all_departments: boolean;
  created_at: string;
}

export const SUGGESTED_DEPARTMENTS = [
  'Management / Business Owner',
  'IT Security',
  'Procurement / Vendor Management',
  'Compliance',
  'Legal',
  'Finance',
  'Operations',
  'HR / People',
  'Engineering / Development',
];

const DEPARTMENTS_CACHE_TTL_MS = 30_000;

const departmentsCache = new Map<string, { data: Department[]; expiresAt: number }>();
const departmentsInflight = new Map<string, Promise<Department[]>>();

export function hydrateDepartmentsCache(orgId: string, departments: Department[]) {
  departmentsCache.set(orgId, {
    data: departments,
    expiresAt: Date.now() + DEPARTMENTS_CACHE_TTL_MS,
  });
}

export function getCachedDepartments(orgId?: string | null): Department[] {
  if (!orgId) {
    return [];
  }

  const cached = departmentsCache.get(orgId);
  return cached && cached.expiresAt > Date.now() ? cached.data : [];
}

async function fetchDepartmentsForOrg(orgId: string, force = false) {
  if (force) {
    departmentsCache.delete(orgId);
  }

  const cached = departmentsCache.get(orgId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  let inflight = departmentsInflight.get(orgId);
  if (!inflight) {
    inflight = supabase
      .from('organization_departments')
      .select('*')
      .eq('organization_id', orgId)
      .order('display_order', { ascending: true })
      .then(({ data, error }) => {
        if (error) {
          throw error;
        }

        const nextDepartments = (data || []) as Department[];
        hydrateDepartmentsCache(orgId, nextDepartments);
        return nextDepartments;
      })
      .finally(() => {
        departmentsInflight.delete(orgId);
      });

    departmentsInflight.set(orgId, inflight);
  }

  return inflight;
}

export function resetDepartmentsCacheForTests() {
  departmentsCache.clear();
  departmentsInflight.clear();
}

export function useDepartments(orgId?: string | null) {
  const [departments, setDepartments] = useState<Department[]>(() => {
    if (!orgId) {
      return [];
    }

    const cached = departmentsCache.get(orgId);
    return cached && cached.expiresAt > Date.now() ? cached.data : [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    if (!orgId) {
      return false;
    }

    const cached = departmentsCache.get(orgId);
    return !(cached && cached.expiresAt > Date.now());
  });

  const fetchDepartments = useCallback(async (force = false) => {
    if (!orgId) {
      setDepartments([]);
      setIsLoading(false);
      return;
    }

    const cached = departmentsCache.get(orgId);
    const shouldUseCached = !force && cached && cached.expiresAt > Date.now();
    setIsLoading(!shouldUseCached);

    try {
      const nextDepartments = await fetchDepartmentsForOrg(orgId, force);
      setDepartments(nextDepartments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  return {
    departments,
    isLoading,
    refetch: () => fetchDepartments(true),
  };
}

export function useUserDepartments(orgId?: string | null, userId?: string | null) {
  const [assignments, setAssignments] = useState<UserDepartmentAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAllAccess, setHasAllAccess] = useState(false);

  const fetchAssignments = useCallback(async () => {
    if (!orgId || !userId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_department_assignments')
        .select('*')
        .eq('organization_id', orgId)
        .eq('user_id', userId);

      if (error) throw error;
      const items = (data || []) as UserDepartmentAssignment[];
      setAssignments(items);
      setHasAllAccess(items.some(a => a.all_departments));
    } catch (error) {
      console.error('Error fetching user department assignments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, userId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  return { assignments, hasAllAccess, isLoading, refetch: fetchAssignments };
}
